import { useEffect, useState } from "react";

interface AdminUser {
  id: string;
  email: string;
  role: string;
  groupIds: string[];
}

interface AdminGroup {
  id: string;
  name: string;
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`요청 실패: ${url} (${res.status})`);
  return (await res.json()) as T;
}

async function postJson(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function deleteJson(url: string, body?: unknown): Promise<Response> {
  return fetch(url, {
    method: "DELETE",
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function AdminScreen() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [groups, setGroups] = useState<AdminGroup[]>([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    try {
      const [usersRes, groupsRes] = await Promise.all([
        getJson<{ users: AdminUser[] }>("/api/admin/users"),
        getJson<{ groups: AdminGroup[] }>("/api/admin/groups"),
      ]);
      setUsers(usersRes.users);
      setGroups(groupsRes.groups);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: 마운트 시 1회만 초기 로드한다.
  useEffect(() => {
    reload();
  }, []);

  async function createUser() {
    if (newUserEmail.trim() === "") return;
    const res = await postJson("/api/admin/users", { email: newUserEmail.trim() });
    if (res.ok) {
      setNewUserEmail("");
      await reload();
    } else {
      setError((await res.json()).error ?? "사용자 생성 실패");
    }
  }

  async function deleteUser(id: string) {
    const res = await deleteJson(`/api/admin/users/${id}`);
    if (res.ok) {
      await reload();
    } else {
      setError((await res.json()).error ?? "사용자 삭제 실패");
    }
  }

  async function createGroup() {
    if (newGroupName.trim() === "") return;
    const res = await postJson("/api/admin/groups", { name: newGroupName.trim() });
    if (res.ok) {
      setNewGroupName("");
      await reload();
    } else {
      setError((await res.json()).error ?? "그룹 생성 실패");
    }
  }

  async function deleteGroup(id: string) {
    const res = await deleteJson(`/api/admin/groups/${id}`);
    if (res.ok) {
      await reload();
    } else {
      setError((await res.json()).error ?? "그룹 삭제 실패");
    }
  }

  async function toggleMembership(userId: string, groupId: string, hasIt: boolean) {
    const res = hasIt
      ? await deleteJson("/api/admin/user-groups", { userId, groupId })
      : await postJson("/api/admin/user-groups", { userId, groupId });
    if (res.ok) {
      await reload();
    } else {
      setError((await res.json()).error ?? "소속 변경 실패");
    }
  }

  async function copyInviteLink() {
    await navigator.clipboard.writeText(window.location.origin);
  }

  return (
    <section>
      {error !== null && <p role="alert">{error}</p>}

      <h2>그룹</h2>
      {groups.length === 0 && <p>그룹이 없습니다.</p>}
      <ul>
        {groups.map((g) => (
          <li key={g.id}>
            {g.name}{" "}
            <button type="button" className="outline contrast" onClick={() => deleteGroup(g.id)}>
              삭제
            </button>
          </li>
        ))}
      </ul>
      <label htmlFor="admin-new-group-name">새 그룹 이름</label>
      <input
        id="admin-new-group-name"
        value={newGroupName}
        onChange={(e) => setNewGroupName(e.target.value)}
        placeholder="예: dev-team"
      />
      <button type="button" onClick={createGroup}>
        그룹 생성
      </button>

      <div className="list-head">
        <h2>사용자</h2>
        <button type="button" className="secondary" onClick={copyInviteLink}>
          초대 접속 주소 복사
        </button>
      </div>
      <table>
        <caption>사용자 목록</caption>
        <thead>
          <tr>
            <th scope="col">이메일</th>
            <th scope="col">역할</th>
            <th scope="col">그룹</th>
            <th scope="col"> </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.role === "admin" ? "관리자" : "사용자"}</td>
              <td>
                {groups.map((g) => {
                  const hasIt = u.groupIds.includes(g.id);
                  return (
                    <label key={g.id} style={{ marginRight: "0.5rem" }}>
                      <input type="checkbox" checked={hasIt} onChange={() => toggleMembership(u.id, g.id, hasIt)} />
                      {g.name}
                    </label>
                  );
                })}
              </td>
              <td>
                <button type="button" className="outline contrast" onClick={() => deleteUser(u.id)}>
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label htmlFor="admin-new-user-email">새 사용자 이메일</label>
      <input
        id="admin-new-user-email"
        value={newUserEmail}
        onChange={(e) => setNewUserEmail(e.target.value)}
        placeholder="user@example.com"
      />
      <button type="button" onClick={createUser}>
        사용자 생성
      </button>
    </section>
  );
}
